import type { ShopperBasketsTypes } from 'commerce-sdk-isomorphic'
import { defineStore } from 'pinia'
import { SelectedPCPOption } from '~/components/Product/ProfessionalCarePlan.vue'
import { PAYMENT_METHOD_IDS } from '~/config/payment'

export type addItemOpts = createBasketWithItemOpts & {
  /* Supplementary data for AddToCartPopup */
  popup?: {
    selectedPcpOption: SelectedPCPOption | null
    product: any
    isGiftWithPurchase?: boolean
  }
}

export const useBasketsStore = defineStore('baskets', () => {
  const baskets = ref<BasketWithProductData[]>([])
  const loading = ref(false)
  const error = ref()
  const initFailed = ref(false)
  const isShowAddToBagPopup = ref(false)
  const basket = computed<BasketWithProductData | undefined>(() => baskets.value?.[0])
  const count = computed(() => basket.value?.productItems?.length || 0)
  const basketId = computed(() => basket.value?.basketId)

  async function init() {
    initFailed.value = false
    try {
      // Returns an Error rather than throwing; a guest with no customer id yet returns nothing at
      // all, which is an absent cart rather than a failed load.
      const result = await useBasket().getCustomerBasketsWithImage()
      initFailed.value = result instanceof Error
    } catch (err) {
      console.error(err)
      error.value = err
      initFailed.value = true
    }
  }

  async function setBasketRemainingBalance() {
    if (!basket.value) return
    const gcPITotal =
      basket.value.paymentInstruments
        ?.filter(
          (paymentInstrument: ShopperBasketsTypes.OrderPaymentInstrument) =>
            paymentInstrument.paymentMethodId === PAYMENT_METHOD_IDS.givex,
        )
        .reduce((total: number, paymentInstrument: ShopperBasketsTypes.OrderPaymentInstrument) => {
          const piTotal = paymentInstrument.amount || 0
          return total + piTotal
        }, 0) || 0

    const orderTotal = basket.value.orderTotal || 0
    if (gcPITotal > orderTotal) {
      // handles edge case where customer has added gift cards, then updated their cart to remove an item.
      await useGivex().removeAllGivexCards()
      basket.value.remainingBalance = orderTotal
      return
    }
    basket.value.remainingBalance = orderTotal >= gcPITotal ? Math.max(0, orderTotal - gcPITotal) : 0
  }

  async function setBaskets(b: BasketWithProductData[]) {
    if (!Array.isArray(b) || !b?.[0]?.basketId) {
      console.log('Invalid baskets data', b)
      return
    }
    baskets.value = b
    await setBasketRemainingBalance()
  }

  async function setBasket(b: ShopperBasketsTypes.Basket) {
    if (!b?.basketId) {
      console.log('Invalid basket data', b)
      return
    }

    if (Array.isArray(baskets.value) && baskets.value.length > 0) {
      const updatedBaskets = [...baskets.value]
      try {
        const updatedBasket = await useBasket().updateBasketWithProductData(b)
        updatedBaskets[0] = updatedBasket
        baskets.value = updatedBaskets
      } catch (err) {
        console.error(err)
        error.value = err
      }
    } else {
      baskets.value = [b]
    }

    await setBasketRemainingBalance()
  }

  async function addItem(opts: addItemOpts) {
    const { createBasketWithItem, addItemToBasket, updateBasketWithProductData } = useBasket()
    error.value = null
    loading.value = true
    try {
      // A click can land before the layout's first basket load; adding then would start a second basket.
      await whenLayoutStoresSettled()
      const responseBasket = basketId.value
        ? await addItemToBasket({ basketId: basketId.value, ...opts })
        : await createBasketWithItem(opts)

      if (responseBasket instanceof Error) {
        throw responseBasket
      }

      const updatedBasket = await updateBasketWithProductData(responseBasket)
      await setBaskets([updatedBasket])

      // Braze Integration - Add to Bag Event
      if ('productItems' in responseBasket) {
        useBrazeTracking().logCustomEvent('add_to_bag', {
          // Track product added
          product_id: opts.productId,
          product_name: opts.popup?.product.name,
          product_price: opts.popup?.product.price,
          product_image_url: findPrimaryAngle(opts.popup?.product.images),
          product_primary_category: opts.popup?.product?.c_mHJPrimaryCategory,

          // Add full basket context
          basket_id: responseBasket.basketId,
          basket_total: responseBasket.orderTotal,
          basket_currency: responseBasket.currency,
          products_in_basket: responseBasket.productItems, // NOTE: Braze Phase 1 has approved sending all line item data as is
        })
      }

      isShowAddToBagPopup.value = !!opts.popup
    } catch (err) {
      console.error(err)
      error.value = err
    } finally {
      loading.value = false
    }
  }

  async function updateItem(opts: UpdateItemOpts) {
    const { updateItemInBasket } = useBasket()
    error.value = null
    loading.value = true
    try {
      if (!basketId.value) {
        throw new Error('No basketId found')
      }
      const responseBasket = await updateItemInBasket({ basketId: basketId.value, ...opts })

      if (responseBasket) {
        // @ts-ignore
        const updatedBasket = await useBasket().updateBasketWithProductData(responseBasket)
        await setBaskets([updatedBasket]) /* Baskets expected to be an Array */
      }

      // Braze Integration - Update Basket Product Line Item Event
      // NOTE: Business decision to use the same add_to_bag event for both add and update, we will not call remove_from_bag first
      if ('productItems' in responseBasket) {
        const updatedItem = responseBasket.productItems?.find((item) => item.itemId === opts.itemId)

        useBrazeTracking().logCustomEvent('add_to_bag', {
          // Track product added
          product_id: updatedItem?.productId,
          product_name: updatedItem?.productName,
          product_price: updatedItem?.price,
          // NOTE: product image and primary category not available in ProductItem context

          // Add full basket context
          basket_id: responseBasket.basketId,
          basket_total: responseBasket.orderTotal,
          basket_currency: responseBasket.currency,
          products_in_basket: responseBasket.productItems, // NOTE: Braze Phase 1 has approved sending all line item data as is
        })
      }

      return responseBasket
    } catch (err) {
      console.error(err)
      error.value = err
    } finally {
      loading.value = false
    }
  }

  function isItemInBasket(sku: string) {
    return basket.value?.productItems?.some((item: ShopperBasketsTypes.ProductItem) => item.productId === sku) || false
  }

  function getItem(sku: string) {
    return basket.value?.productItems?.findLast((item: ShopperBasketsTypes.ProductItem) => item.productId === sku)
  }

  async function mergeBaskets() {
    const { mergeBasket, updateBasketWithProductData, getCustomerBasketsWithImage } = useBasket()
    error.value = null
    loading.value = true
    try {
      // These return an Error rather than throwing it; without the throw an unread cart would pass as loaded.
      if (baskets.value.length > 0) {
        const responseBasket = await mergeBasket()
        if (responseBasket instanceof Error) throw responseBasket

        if (responseBasket) {
          const updatedBasket = await updateBasketWithProductData(responseBasket)
          if (updatedBasket instanceof Error) throw updatedBasket
          await setBaskets([updatedBasket]) /* Baskets expected to be an Array */
        }
      } else {
        const result = await getCustomerBasketsWithImage()
        if (result instanceof Error) throw result
      }
    } catch (err) {
      console.error(err)
      error.value = err
    } finally {
      loading.value = false
    }
  }

  const reset = () => {
    baskets.value = []
  }

  return {
    baskets,
    basket,
    count,
    basketId,
    loading,
    error,
    initFailed,
    isShowAddToBagPopup,
    init,
    setBaskets,
    setBasket,
    addItem,
    updateItem,
    getItem,
    isItemInBasket,
    mergeBaskets,
    reset,
  }
})
